import { useState, useEffect, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import {
  FileCheck,
  RefreshCw,
  Eye,
  X,
  Check,
  Ban,
  AlertTriangle,
  ExternalLink,
  User,
  MessageSquare,
} from 'lucide-react';

import {
  getDisputedEvidenceThunk,
  getEvidenceFlagsThunk,
  resolveFlagThunk,
  adminUpdateEvidenceStatusThunk,
} from '../../../redux/posts/postEvidence/postEvidenceActions';
import CredibilityChip from '../../../components/Credibility/CredibilityChip';

// Mirrors AdminModerationComponent.jsx's established patterns (fetch-on-mount,
// theme classes, error banner, stat cards, table + detail modal) rather than
// inventing a new admin UI shape. Deliberately smaller in scope than that
// component: the disputed-evidence endpoint (GET /evidence/admin/pending-review)
// only supports skip/limit, no status/type/reason/search filters, so this
// doesn't build filter UI the backend can't actually serve.
const AdminEvidenceModerationComponent = ({ darkMode = true }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(20);
  const [detailEvidenceId, setDetailEvidenceId] = useState(null);

  const disputed = useSelector((state) => state.postEvidence?.disputed || []);
  const loading = useSelector((state) => state.postEvidence?.loading || false);
  const error = useSelector((state) => state.postEvidence?.error);
  const flagsByEvidence = useSelector((state) => state.postEvidence?.flagsByEvidence || {});

  const themeClasses = {
    cardBg: darkMode ? 'bg-gray-800' : 'bg-white',
    text: darkMode ? 'text-white' : 'text-gray-900',
    textSecondary: darkMode ? 'text-gray-300' : 'text-gray-600',
    textMuted: darkMode ? 'text-gray-400' : 'text-gray-500',
    border: darkMode ? 'border-gray-700' : 'border-gray-200',
    hoverBg: darkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-50',
    buttonPrimary: 'bg-blue-600 hover:bg-blue-700 text-white',
    buttonSecondary: darkMode ? 'bg-gray-700 hover:bg-gray-600 text-gray-300' : 'bg-gray-200 hover:bg-gray-300 text-gray-700',
    tableHeaderBg: darkMode ? 'bg-gray-700' : 'bg-gray-50',
    tableRowHover: darkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-50',
  };

  const fetchQueue = useCallback((page) => {
    const skip = (page - 1) * pageSize;
    dispatch(getDisputedEvidenceThunk({ skip, limit: pageSize }));
  }, [dispatch, pageSize]);

  useEffect(() => {
    fetchQueue(currentPage);
  }, [currentPage, fetchQueue]);

  const openDetail = (evidenceId) => {
    setDetailEvidenceId(evidenceId);
    dispatch(getEvidenceFlagsThunk(evidenceId));
  };

  // .unwrap() + try/catch, not a bare `await dispatch(...)`: a plain thunk
  // dispatch never throws (it always resolves, fulfilled or rejected), so
  // code after a bare await runs unconditionally on failure too -- the
  // refetch that used to follow unconditionally would immediately clear
  // `state.postEvidence.error` via its own `.pending` case before an admin
  // ever saw it, silently erasing the exact error this UI exists to surface
  // (reproduced live: a forced 500 on the status-update call left the modal
  // closed and the error banner empty). Only refetch/close on real success now.
  const handleResolveFlag = async (flagId, isUpheld) => {
    try {
      await dispatch(resolveFlagThunk({ flagId, is_upheld: isUpheld })).unwrap();
      if (detailEvidenceId) dispatch(getEvidenceFlagsThunk(detailEvidenceId));
    } catch {
      // error is already in state.postEvidence.error via the thunk's own
      // .rejected case; the top-level error banner renders it, and the
      // modal deliberately stays open so the admin can see and retry.
    }
  };

  const handleUpdateStatus = async (evidenceId, newStatus) => {
    try {
      await dispatch(adminUpdateEvidenceStatusThunk({ evidenceId, new_status: newStatus })).unwrap();
      setDetailEvidenceId(null);
      fetchQueue(currentPage);
    } catch {
      // Same reasoning as handleResolveFlag above -- stay put, let the
      // error banner show, don't silently swallow the failure.
    }
  };

  const statusBadge = (status) => {
    const badges = {
      pending: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300',
      verified: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300',
      broken: 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300',
      disputed: 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-300',
      removed: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300',
    };
    return badges[status] || 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300';
  };

  const detailFlags = detailEvidenceId ? (flagsByEvidence[detailEvidenceId] || []) : [];
  const detailEvidence = disputed.find((e) => e.id === detailEvidenceId);

  return (
    <div className="space-y-6">
      {error && (
        <div className={`${darkMode ? 'bg-red-900/50 border-red-800' : 'bg-red-50 border-red-200'} border rounded-lg p-4`}>
          <div className="flex items-center">
            <AlertTriangle className={`h-5 w-5 ${darkMode ? 'text-red-400' : 'text-red-600'} mr-2`} />
            <span className={`${darkMode ? 'text-red-300' : 'text-red-800'} font-medium`}>{error}</span>
          </div>
        </div>
      )}

      <div className={`${themeClasses.cardBg} rounded-xl border ${themeClasses.border} shadow-sm`}>
        <div className="p-6 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <h2 className={`text-xl font-semibold ${themeClasses.text} flex items-center`}>
              <FileCheck className="h-6 w-6 mr-2" />
              {t('admin.evidenceModeration.title')}
            </h2>
            <span className={`px-2 py-1 text-xs rounded-full ${darkMode ? 'bg-gray-700 text-gray-300' : 'bg-gray-200 text-gray-700'}`}>
              {t('admin.newsModeration.onThisPage', { count: disputed.length })}
            </span>
          </div>
          <button
            onClick={() => fetchQueue(currentPage)}
            disabled={loading}
            className={`flex items-center px-4 py-2 ${themeClasses.buttonSecondary} rounded-lg transition-colors disabled:opacity-50`}
          >
            <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            {t('admin.newsModeration.refresh')}
          </button>
        </div>

        <div className="overflow-x-auto">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <RefreshCw className="h-6 w-6 animate-spin mr-2 text-blue-600" />
              <span className={themeClasses.text}>{t('admin.evidenceModeration.loadingEvidence')}</span>
            </div>
          ) : disputed.length > 0 ? (
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
              <thead className={themeClasses.tableHeaderBg}>
                <tr>
                  <th className={`px-6 py-3 text-left text-xs font-medium ${themeClasses.textMuted} uppercase tracking-wider`}>{t('admin.evidenceModeration.table.evidence')}</th>
                  <th className={`px-6 py-3 text-left text-xs font-medium ${themeClasses.textMuted} uppercase tracking-wider`}>{t('admin.evidenceModeration.table.type')}</th>
                  <th className={`px-6 py-3 text-left text-xs font-medium ${themeClasses.textMuted} uppercase tracking-wider`}>{t('admin.evidenceModeration.table.status')}</th>
                  <th className={`px-6 py-3 text-left text-xs font-medium ${themeClasses.textMuted} uppercase tracking-wider`}>{t('admin.evidenceModeration.table.author')}</th>
                  <th className={`px-6 py-3 text-left text-xs font-medium ${themeClasses.textMuted} uppercase tracking-wider`}>{t('admin.evidenceModeration.table.actions')}</th>
                </tr>
              </thead>
              <tbody className={`divide-y ${darkMode ? 'divide-gray-700' : 'divide-gray-200'}`}>
                {disputed.map((ev) => (
                  <tr key={ev.id} className={themeClasses.tableRowHover}>
                    <td className="px-6 py-4">
                      <div className={`text-sm font-medium ${themeClasses.text}`}>#{ev.id}: {ev.title || t('admin.evidenceModeration.noTitle')}</div>
                      <div className={`text-xs ${themeClasses.textMuted} truncate max-w-xs`}>{ev.excerpt}</div>
                    </td>
                    <td className={`px-6 py-4 text-sm ${themeClasses.textSecondary}`}>
                      {ev.evidence_type?.replace('_', ' ')}
                    </td>
                    <td className="px-6 py-4">
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${statusBadge(ev.status)}`}>
                        {t(`admin.evidenceModeration.statusLabels.${ev.status}`, { defaultValue: ev.status })}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <div className={`text-sm ${themeClasses.textMuted} flex items-center gap-1.5 flex-wrap`}>
                        <User className="h-3 w-3" /> {ev.author_username ? t('admin.evidenceModeration.authorFallback', { username: ev.author_username }) : t('admin.evidenceModeration.userFallback', { id: ev.user_id })}
                        <CredibilityChip cred={ev.author_credibility} size="xs" showAccuracy={false} />
                      </div>
                      <div className={`text-xs ${themeClasses.textMuted} flex items-center`}>
                        <MessageSquare className="h-3 w-3 mr-1" />
                        {ev.group_post_id ? t('admin.evidenceModeration.groupPostNumber', { id: ev.group_post_id }) : t('admin.evidenceModeration.postNumber', { id: ev.post_id })}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <button
                        onClick={() => openDetail(ev.id)}
                        className="p-1 text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 rounded transition-colors"
                        title={t('admin.evidenceModeration.viewDetailsAndFlags')}
                      >
                        <Eye className="h-4 w-4" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <div className={`text-center py-12 ${themeClasses.textMuted}`}>
              <FileCheck className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p className="text-lg font-medium">{t('admin.evidenceModeration.noDisputedEvidence')}</p>
            </div>
          )}
        </div>

        <div className="px-6 py-4 border-t border-gray-200 dark:border-gray-700 flex items-center justify-end space-x-2">
          <button
            onClick={() => setCurrentPage((p) => Math.max(p - 1, 1))}
            disabled={currentPage === 1}
            className={`px-3 py-2 ${themeClasses.buttonSecondary} rounded-lg transition-colors disabled:opacity-50`}
          >
            {t('admin.newsModeration.previous')}
          </button>
          <span className={`px-3 py-2 text-sm ${themeClasses.text}`}>{t('admin.newsModeration.page', { page: currentPage })}</span>
          <button
            onClick={() => setCurrentPage((p) => p + 1)}
            disabled={disputed.length < pageSize}
            className={`px-3 py-2 ${themeClasses.buttonSecondary} rounded-lg transition-colors disabled:opacity-50`}
          >
            {t('admin.newsModeration.next')}
          </button>
        </div>
      </div>

      {/* Detail modal -- mirrors AdminModerationComponent.jsx's modal pattern */}
      {detailEvidenceId && detailEvidence && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className={`${themeClasses.cardBg} rounded-xl border ${themeClasses.border} p-6 w-full max-w-lg mx-4 shadow-xl max-h-[85vh] overflow-y-auto`}>
            <div className="flex justify-between items-center mb-4">
              <h3 className={`text-lg font-semibold ${themeClasses.text}`}>{t('admin.evidenceModeration.evidenceNumber', { id: detailEvidence.id })}</h3>
              <button onClick={() => setDetailEvidenceId(null)} className={`${themeClasses.textMuted} hover:${themeClasses.text}`}>
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="space-y-3 mb-6">
              <div className={`text-sm font-medium ${themeClasses.text}`}>{detailEvidence.title || t('admin.evidenceModeration.noTitle')}</div>
              {detailEvidence.source_url && (
                <a
                  href={detailEvidence.source_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-sm text-blue-500 hover:text-blue-400 flex items-center"
                >
                  <ExternalLink className="h-3 w-3 mr-1" /> {detailEvidence.source_url}
                </a>
              )}
              <p className={`text-sm ${themeClasses.textSecondary}`}>{detailEvidence.excerpt}</p>
              {detailEvidence.relevance_note && (
                <p className={`text-xs ${themeClasses.textMuted} italic`}>{t('admin.evidenceModeration.relevancePrefix')} {detailEvidence.relevance_note}</p>
              )}
            </div>

            <div className="mb-4">
              <h4 className={`text-sm font-semibold ${themeClasses.text} mb-2`}>{t('admin.evidenceModeration.flagsHeading')}</h4>
              {detailFlags.length === 0 ? (
                <p className={`text-sm ${themeClasses.textMuted}`}>{t('admin.evidenceModeration.noFlagsFound')}</p>
              ) : (
                <div className="space-y-2">
                  {detailFlags.map((flag) => (
                    <div key={flag.id} className={`p-3 rounded-lg border ${themeClasses.border} ${darkMode ? 'bg-gray-900/50' : 'bg-gray-50'}`}>
                      <div className="flex items-center justify-between">
                        <span className={`text-sm font-medium ${themeClasses.text}`}>
                          {flag.flag_type?.replace('_', ' ')}
                        </span>
                        {flag.is_upheld === null || flag.is_upheld === undefined ? (
                          <span className="text-xs px-2 py-0.5 rounded-full bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300">
                            {t('admin.evidenceModeration.pendingReview')}
                          </span>
                        ) : (
                          <span className={`text-xs px-2 py-0.5 rounded-full ${flag.is_upheld ? 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300' : 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300'}`}>
                            {flag.is_upheld ? t('admin.evidenceModeration.upheld') : t('admin.evidenceModeration.rejected')}
                          </span>
                        )}
                      </div>
                      {flag.reason && <p className={`text-xs ${themeClasses.textMuted} mt-1`}>{flag.reason}</p>}
                      {(flag.is_upheld === null || flag.is_upheld === undefined) && (
                        <div className="flex gap-2 mt-2">
                          <button
                            onClick={() => handleResolveFlag(flag.id, true)}
                            className="px-2 py-1 text-xs bg-red-600 text-white rounded hover:bg-red-700 transition-colors"
                          >
                            {t('admin.evidenceModeration.uphold')}
                          </button>
                          <button
                            onClick={() => handleResolveFlag(flag.id, false)}
                            className="px-2 py-1 text-xs bg-green-600 text-white rounded hover:bg-green-700 transition-colors"
                          >
                            {t('admin.evidenceModeration.reject')}
                          </button>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div>
              <h4 className={`text-sm font-semibold ${themeClasses.text} mb-2`}>{t('admin.evidenceModeration.updateStatusHeading')}</h4>
              <div className="flex flex-wrap gap-2">
                <button
                  onClick={() => handleUpdateStatus(detailEvidence.id, 'verified')}
                  className="flex items-center px-3 py-1.5 text-xs bg-green-600 text-white rounded hover:bg-green-700 transition-colors"
                >
                  <Check className="h-3 w-3 mr-1" /> {t('admin.evidenceModeration.markVerified')}
                </button>
                <button
                  onClick={() => handleUpdateStatus(detailEvidence.id, 'removed')}
                  className="flex items-center px-3 py-1.5 text-xs bg-red-600 text-white rounded hover:bg-red-700 transition-colors"
                >
                  <Ban className="h-3 w-3 mr-1" /> {t('admin.evidenceModeration.remove')}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminEvidenceModerationComponent;
